<template>
  <div v-if="currentUser">
    <UiUserMenu
      data-testid="user-menu-trigger"
      :size="triggerSize <= 28 ? 'sm' : triggerSize >= 40 ? 'lg' : 'md'"
      v-model:open="isOpen"
      :name="displayName"
      :secondary-text="currentUser.username ? '@' + currentUser.username : undefined"
      :groups="menuGroups"
      @select="selectMenuAction"
    >
      <UiSeparator />
      <UiButton
        variant="ghost"
        class="user-menu-action w-full text-left flex items-center justify-start gap-2 disabled:opacity-60"
        @click="cleanCacheAndRefresh"
        :disabled="cleaningCache"
      >
        <i class="fa-solid w-4" :class="cleaningCache ? 'fa-circle-notch fa-spin' : 'fa-rotate'"></i>
        {{ cleaningCache ? 'Cleaning cache…' : 'Clean Cache and Refresh' }}
      </UiButton>
      <UiButton
        variant="ghost"
        aria-label="Logout"
        class="user-menu-action user-menu-action--danger w-full text-left flex items-center justify-start gap-2"
        @click="handleLogout"
      >
        <i class="fa-solid fa-sign-out-alt w-4"></i> Logout
      </UiButton>
    </UiUserMenu>
  </div>
  <div v-else>
    <UiButton
      variant="primary"
      size="sm"
      shape="pill"
      class="text-xs font-semibold px-3"
      @click="$emit('open-login')"
    >
      <i class="fa-brands fa-telegram mr-1.5"></i> Log in
    </UiButton>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { UiUserMenu, UiButton, UiSeparator } from '../ui';
import { useAuth } from '../composables/useAuth';
import { mediaCache } from '../services/mediaCacheService';

withDefaults(defineProps<{ triggerSize?: number }>(), { triggerSize: 32 });

const emit = defineEmits<{
  'open-preferences': [];
  'open-login': [];
}>();

const isOpen = ref(false);
const cleaningCache = ref(false);
const { currentUser, logout } = useAuth();

const displayName = computed(() => {
  return currentUser.value?.first_name || currentUser.value?.username || 'Learner';
});

const menuGroups = computed(() => [
  {
    actions: [
      {
        id: 'preferences',
        label: 'Preferences',
        icon: 'fa-solid fa-sliders',
      },
    ],
  },
]);

function selectMenuAction(id: string) {
  isOpen.value = false;
  if (id === 'preferences') {
    emit('open-preferences');
  }
}

async function cleanCacheAndRefresh() {
  cleaningCache.value = true;
  isOpen.value = false;
  try {
    await mediaCache.clearCache();
    if ('caches' in window) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((k) => window.caches.delete(k)));
    }
  } catch (err) {
    console.warn('Cache clear warning:', err);
  } finally {
    window.location.reload();
  }
}

function handleLogout() {
  isOpen.value = false;
  logout();
}
</script>

<style scoped>
.user-menu-action {
  border: 0;
  color: var(--ui-text);
  font-weight: 400;
  padding: 0.5rem 0.75rem;
}
.user-menu-action i {
  flex: 0 0 1rem;
  width: 1rem;
  text-align: center;
}
.user-menu-action--danger {
  color: var(--ui-danger);
}
.user-menu-action--danger:hover {
  background-color: var(--ui-danger-surface, rgba(239, 68, 68, 0.1));
}
</style>
